// components/VerdictMemeTemplates.jsx
// 🎨 ULTIMATE MEME TEMPLATE SYSTEM
// 5 distinct visual styles designed for maximum shareability

import React from 'react';

// =====================================================
// TEMPLATE 1: COURTROOM VERDICT (Official/Serious)
// =====================================================
export const CourtroomTemplate = ({ trial, verdict, format = 'square' }) => {
  const winner = verdict.winner === 'A' ? trial.sideA : trial.sideB;
  const loser = verdict.winner === 'A' ? trial.sideB : trial.sideA;
  const winnerPct = Math.round(verdict.winnerPct || 50);
  const punchline = verdict.punchline || 'The jury has spoken.';
  
  const isStory = format === 'story';
  const width = isStory ? '1080px' : '1080px';
  const height = isStory ? '1920px' : '1080px';

  return (
    <div style={{
      width,
      height,
      background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1e 100%)',
      fontFamily: '"Georgia", "Times New Roman", serif',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: isStory ? '80px 60px' : '60px 80px',
      boxSizing: 'border-box',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background texture */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url("data:image/svg+xml,%3Csvg width="100" height="100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M0 0h100v100H0z" fill="%23000" fill-opacity=".03"/%3E%3C/svg%3E")',
        opacity: 0.5
      }} />

      {/* Top seal */}
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{
          width: '120px',
          height: '120px',
          margin: '0 auto 20px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #d4af37 0%, #f4e5b7 50%, #d4af37 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '64px',
          boxShadow: '0 10px 40px rgba(212, 175, 55, 0.4)',
          border: '4px solid #f4e5b7'
        }}>
          ⚖️
        </div>
        <div style={{
          fontSize: '28px',
          fontWeight: '700',
          letterSpacing: '4px',
          color: '#d4af37',
          textTransform: 'uppercase',
          textShadow: '0 2px 4px rgba(0,0,0,0.5)'
        }}>
          Official Verdict
        </div>
        <div style={{
          fontSize: '16px',
          color: '#999',
          marginTop: '8px',
          letterSpacing: '2px'
        }}>
          CASE #{Math.floor(Math.random() * 90000) + 10000}
        </div>
      </div>

      {/* Trial title */}
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{
          fontSize: isStory ? '56px' : '64px',
          fontWeight: '900',
          lineHeight: '1.2',
          marginBottom: '40px',
          textShadow: '0 4px 8px rgba(0,0,0,0.8)'
        }}>
          {trial.title}
        </div>
      </div>

      {/* Verdict box */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(244, 229, 183, 0.1) 100%)',
        border: '3px solid #d4af37',
        borderRadius: '20px',
        padding: isStory ? '50px 40px' : '60px 50px',
        textAlign: 'center',
        position: 'relative',
        zIndex: 1,
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
      }}>
        {/* Winner */}
        <div style={{
          fontSize: isStory ? '72px' : '96px',
          fontWeight: '900',
          color: '#d4af37',
          marginBottom: '20px',
          textTransform: 'uppercase',
          letterSpacing: '2px',
          textShadow: '0 4px 12px rgba(212, 175, 55, 0.6)'
        }}>
          {winner}
        </div>

        {/* Percentage */}
        <div style={{
          fontSize: isStory ? '120px' : '160px',
          fontWeight: '900',
          color: '#fff',
          lineHeight: '1',
          textShadow: '0 6px 20px rgba(255,255,255,0.3)'
        }}>
          {winnerPct}%
        </div>

        {/* Loser */}
        <div style={{
          fontSize: isStory ? '48px' : '56px',
          fontWeight: '700',
          color: '#666',
          marginTop: '20px',
          textDecoration: 'line-through',
          opacity: 0.5
        }}>
          {loser}
        </div>
      </div>

      {/* Punchline */}
      <div style={{
        textAlign: 'center',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          fontSize: isStory ? '32px' : '36px',
          fontWeight: '600',
          fontStyle: 'italic',
          color: '#ccc',
          lineHeight: '1.4',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          "{punchline}"
        </div>
      </div>

      {/* Stats footer */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: '2px solid #d4af37',
        paddingTop: '30px',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{ fontSize: '20px', color: '#999' }}>
          {trial.totalVotes || 0} Jurors
        </div>
        <div style={{
          fontSize: '32px',
          fontWeight: '900',
          color: '#d4af37',
          letterSpacing: '2px'
        }}>
          GWEFFIT
        </div>
        <div style={{ fontSize: '20px', color: '#999' }}>
          gweffit.com
        </div>
      </div>
    </div>
  );
};

// =====================================================
// TEMPLATE 2: SPORTS ESPN STYLE (Bold/Athletic)
// =====================================================
export const SportsTemplate = ({ trial, verdict, format = 'square' }) => {
  const winner = verdict.winner === 'A' ? trial.sideA : trial.sideB;
  const loser = verdict.winner === 'A' ? trial.sideB : trial.sideA;
  const winnerPct = Math.round(verdict.winnerPct || 50);
  const punchline = verdict.punchline || 'Game over.';
  
  const isStory = format === 'story';
  const width = isStory ? '1080px' : '1080px';
  const height = isStory ? '1920px' : '1080px';

  return (
    <div style={{
      width,
      height,
      background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
      fontFamily: '"Arial Black", "Impact", sans-serif',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: '0',
      boxSizing: 'border-box',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Red banner top */}
      <div style={{
        background: 'linear-gradient(90deg, #ff0000 0%, #cc0000 100%)',
        padding: '20px 40px',
        boxShadow: '0 4px 20px rgba(255,0,0,0.5)'
      }}>
        <div style={{
          fontSize: '28px',
          fontWeight: '900',
          letterSpacing: '3px',
          textTransform: 'uppercase',
          textShadow: '0 2px 4px rgba(0,0,0,0.8)'
        }}>
          🏆 FINAL VERDICT 🏆
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '40px' }}>
        {/* Title */}
        <div style={{
          fontSize: isStory ? '48px' : '56px',
          fontWeight: '900',
          textAlign: 'center',
          marginBottom: '60px',
          textTransform: 'uppercase',
          textShadow: '0 4px 12px rgba(255,255,255,0.3)',
          letterSpacing: '2px'
        }}>
          {trial.title}
        </div>

        {/* Score display */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '60px',
          marginBottom: '60px'
        }}>
          {/* Winner side */}
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{
              fontSize: isStory ? '64px' : '80px',
              fontWeight: '900',
              color: '#00ff00',
              marginBottom: '20px',
              textShadow: '0 0 30px rgba(0,255,0,0.8)'
            }}>
              {winnerPct}
            </div>
            <div style={{
              fontSize: isStory ? '36px' : '42px',
              fontWeight: '900',
              color: '#fff',
              textTransform: 'uppercase'
            }}>
              {winner}
            </div>
            <div style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: '#00ff00',
              color: '#000',
              fontSize: '24px',
              fontWeight: '900',
              display: 'inline-block',
              borderRadius: '8px'
            }}>
              WINNER
            </div>
          </div>

          {/* VS */}
          <div style={{
            fontSize: '64px',
            fontWeight: '900',
            color: '#ff0000',
            textShadow: '0 0 20px rgba(255,0,0,0.8)'
          }}>
            VS
          </div>

          {/* Loser side */}
          <div style={{ textAlign: 'center', flex: 1, opacity: 0.5 }}>
            <div style={{
              fontSize: isStory ? '64px' : '80px',
              fontWeight: '900',
              color: '#666',
              marginBottom: '20px'
            }}>
              {100 - winnerPct}
            </div>
            <div style={{
              fontSize: isStory ? '36px' : '42px',
              fontWeight: '900',
              color: '#999',
              textTransform: 'uppercase',
              textDecoration: 'line-through'
            }}>
              {loser}
            </div>
          </div>
        </div>

        {/* Punchline */}
        <div style={{
          background: 'rgba(255,255,255,0.1)',
          padding: '30px',
          borderLeft: '8px solid #ff0000',
          fontSize: isStory ? '32px' : '38px',
          fontWeight: '700',
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          "{punchline}"
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{
        background: '#ff0000',
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 -4px 20px rgba(255,0,0,0.5)'
      }}>
        <div style={{ fontSize: '20px', fontWeight: '900' }}>
          {trial.totalVotes || 0} VOTES CAST
        </div>
        <div style={{ fontSize: '36px', fontWeight: '900', letterSpacing: '3px' }}>
          GWEFFIT
        </div>
      </div>
    </div>
  );
};

// =====================================================
// TEMPLATE 3: SAVAGE/BRUTAL STYLE (Dark/Edgy)
// =====================================================
export const SavageTemplate = ({ trial, verdict, format = 'square' }) => {
  const winner = verdict.winner === 'A' ? trial.sideA : trial.sideB;
  const loser = verdict.winner === 'A' ? trial.sideB : trial.sideA;
  const winnerPct = Math.round(verdict.winnerPct || 50);
  const punchline = verdict.punchline || 'Absolutely destroyed.';
  
  const isStory = format === 'story';
  const width = isStory ? '1080px' : '1080px';
  const height = isStory ? '1920px' : '1080px';

  return (
    <div style={{
      width,
      height,
      background: 'linear-gradient(135deg, #000000 0%, #1a0000 50%, #000000 100%)',
      fontFamily: '"Impact", "Arial Black", sans-serif',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: isStory ? '80px 60px' : '60px 80px',
      boxSizing: 'border-box',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Fire effects */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        right: '-50%',
        bottom: '-50%',
        background: 'radial-gradient(circle, rgba(255,0,0,0.2) 0%, transparent 70%)',
        animation: 'pulse 3s infinite'
      }} />

      {/* Skull watermark */}
      <div style={{
        position: 'absolute',
        fontSize: '400px',
        opacity: 0.05,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      }}>
        💀
      </div>

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        {/* Flames header */}
        <div style={{
          fontSize: '48px',
          marginBottom: '30px',
          filter: 'drop-shadow(0 0 20px rgba(255,100,0,0.8))'
        }}>
          🔥🔥🔥🔥🔥
        </div>

        {/* Verdict type */}
        <div style={{
          fontSize: '40px',
          fontWeight: '900',
          color: '#ff0000',
          letterSpacing: '6px',
          textTransform: 'uppercase',
          marginBottom: '40px',
          textShadow: '0 0 30px rgba(255,0,0,0.8), 0 4px 8px rgba(0,0,0,0.8)'
        }}>
          ANNIHILATED
        </div>

        {/* Title */}
        <div style={{
          fontSize: isStory ? '52px' : '64px',
          fontWeight: '900',
          marginBottom: '50px',
          textTransform: 'uppercase',
          lineHeight: '1.2',
          textShadow: '0 4px 16px rgba(255,255,255,0.3)'
        }}>
          {trial.title}
        </div>

        {/* Winner announcement */}
        <div style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,0,0,0.3) 50%, transparent 100%)',
          padding: '40px',
          marginBottom: '40px',
          border: '3px solid #ff0000',
          borderLeft: 'none',
          borderRight: 'none',
          boxShadow: '0 0 40px rgba(255,0,0,0.5)'
        }}>
          <div style={{
            fontSize: isStory ? '80px' : '100px',
            fontWeight: '900',
            color: '#ff0000',
            textTransform: 'uppercase',
            textShadow: '0 0 40px rgba(255,0,0,1), 0 8px 16px rgba(0,0,0,0.8)',
            marginBottom: '20px'
          }}>
            {winner}
          </div>
          <div style={{
            fontSize: isStory ? '100px' : '140px',
            fontWeight: '900',
            color: '#fff',
            textShadow: '0 0 30px rgba(255,255,255,0.8)'
          }}>
            {winnerPct}%
          </div>
        </div>

        {/* Loser crossed out */}
        <div style={{
          fontSize: isStory ? '48px' : '56px',
          fontWeight: '900',
          color: '#666',
          textDecoration: 'line-through',
          textTransform: 'uppercase',
          opacity: 0.3,
          marginBottom: '50px'
        }}>
          {loser} ({100 - winnerPct}%)
        </div>

        {/* Brutal punchline */}
        <div style={{
          fontSize: isStory ? '36px' : '42px',
          fontWeight: '900',
          color: '#ff3333',
          fontStyle: 'italic',
          textShadow: '0 4px 8px rgba(0,0,0,0.8)',
          marginBottom: '40px'
        }}>
          "{punchline}"
        </div>

        {/* Flames footer */}
        <div style={{
          fontSize: '48px',
          marginTop: '30px',
          filter: 'drop-shadow(0 0 20px rgba(255,100,0,0.8))'
        }}>
          🔥🔥🔥🔥🔥
        </div>

        {/* Branding */}
        <div style={{
          marginTop: '50px',
          fontSize: '32px',
          fontWeight: '900',
          color: '#ff0000',
          letterSpacing: '4px',
          textShadow: '0 0 20px rgba(255,0,0,0.8)'
        }}>
          GWEFFIT
        </div>
      </div>
    </div>
  );
};

// =====================================================
// TEMPLATE 4: WHOLESOME/FUNNY STYLE (Bright/Cheerful)
// =====================================================
export const WholesomeTemplate = ({ trial, verdict, format = 'square' }) => {
  const winner = verdict.winner === 'A' ? trial.sideA : trial.sideB;
  const loser = verdict.winner === 'A' ? trial.sideB : trial.sideA;
  const winnerPct = Math.round(verdict.winnerPct || 50);
  const punchline = verdict.punchline || 'Everyone wins when we have fun!';
  
  const isStory = format === 'story';
  const width = isStory ? '1080px' : '1080px';
  const height = isStory ? '1920px' : '1080px';

  return (
    <div style={{
      width,
      height,
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
      fontFamily: '"Comic Sans MS", "Chalkboard SE", sans-serif',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: isStory ? '80px 60px' : '60px 80px',
      boxSizing: 'border-box',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Confetti background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.15,
        fontSize: '60px'
      }}>
        {'🎉'.repeat(50)}
      </div>

      {/* Top celebration */}
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ fontSize: '80px', marginBottom: '20px' }}>
          🎊✨🎉✨🎊
        </div>
        <div style={{
          fontSize: '32px',
          fontWeight: '900',
          background: 'rgba(255,255,255,0.3)',
          padding: '15px 30px',
          borderRadius: '50px',
          display: 'inline-block',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
        }}>
          THE PEOPLE HAVE SPOKEN!
        </div>
      </div>

      {/* Main content */}
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        {/* Title */}
        <div style={{
          fontSize: isStory ? '56px' : '68px',
          fontWeight: '900',
          marginBottom: '50px',
          textShadow: '4px 4px 0px rgba(0,0,0,0.2)',
          lineHeight: '1.2'
        }}>
          {trial.title}
        </div>

        {/* Winner card */}
        <div style={{
          background: 'rgba(255,255,255,0.95)',
          color: '#333',
          padding: '50px',
          borderRadius: '40px',
          marginBottom: '40px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          border: '8px solid #fff'
        }}>
          <div style={{
            fontSize: '40px',
            fontWeight: '900',
            color: '#667eea',
            marginBottom: '20px',
            textTransform: 'uppercase'
          }}>
            🏆 Winner! 🏆
          </div>
          <div style={{
            fontSize: isStory ? '72px' : '96px',
            fontWeight: '900',
            color: '#764ba2',
            marginBottom: '20px'
          }}>
            {winner}
          </div>
          <div style={{
            fontSize: isStory ? '120px' : '160px',
            fontWeight: '900',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            lineHeight: '1'
          }}>
            {winnerPct}%
          </div>
        </div>

        {/* Loser (gentle) */}
        <div style={{
          fontSize: isStory ? '36px' : '42px',
          fontWeight: '700',
          opacity: 0.6,
          marginBottom: '40px'
        }}>
          {loser} got {100 - winnerPct}% 💙
        </div>

        {/* Punchline bubble */}
        <div style={{
          background: 'rgba(255,255,255,0.3)',
          padding: '30px 40px',
          borderRadius: '30px',
          fontSize: isStory ? '32px' : '38px',
          fontWeight: '700',
          fontStyle: 'italic',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
        }}>
          "{punchline}"
        </div>
      </div>

      {/* Bottom celebration */}
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div style={{ fontSize: '80px', marginBottom: '20px' }}>
          ✨🎈✨🎈✨
        </div>
        <div style={{
          fontSize: '36px',
          fontWeight: '900',
          textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
        }}>
          GWEFFIT
        </div>
        <div style={{ fontSize: '18px', marginTop: '10px', opacity: 0.8 }}>
          {trial.totalVotes || 0} friends voted!
        </div>
      </div>
    </div>
  );
};

// =====================================================
// TEMPLATE 5: BREAKING NEWS STYLE (Urgent/Serious)
// =====================================================
export const BreakingNewsTemplate = ({ trial, verdict, format = 'square' }) => {
  const winner = verdict.winner === 'A' ? trial.sideA : trial.sideB;
  const loser = verdict.winner === 'A' ? trial.sideB : trial.sideA;
  const winnerPct = Math.round(verdict.winnerPct || 50);
  const punchline = verdict.punchline || 'Developing story...';
  
  const isStory = format === 'story';
  const width = isStory ? '1080px' : '1080px';
  const height = isStory ? '1920px' : '1080px';

  return (
    <div style={{
      width,
      height,
      background: '#0a0a0a',
      fontFamily: '"Arial", "Helvetica", sans-serif',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      padding: '0',
      boxSizing: 'border-box',
      position: 'relative'
    }}>
      {/* Breaking news banner */}
      <div style={{
        background: 'linear-gradient(90deg, #cc0000 0%, #ff0000 50%, #cc0000 100%)',
        padding: '15px 40px',
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        boxShadow: '0 4px 20px rgba(204,0,0,0.8)'
      }}>
        <div style={{
          fontSize: '32px',
          fontWeight: '900',
          letterSpacing: '3px',
          textTransform: 'uppercase',
          flex: 1
        }}>
          ⚠️ BREAKING: VERDICT REACHED
        </div>
        <div style={{
          fontSize: '18px',
          fontWeight: '700',
          background: 'rgba(255,255,255,0.2)',
          padding: '8px 16px',
          borderRadius: '4px'
        }}>
          LIVE
        </div>
      </div>

      {/* Main news content */}
      <div style={{
        flex: 1,
        padding: isStory ? '60px 40px' : '60px 80px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        {/* Headline */}
        <div style={{
          fontSize: isStory ? '48px' : '56px',
          fontWeight: '900',
          marginBottom: '50px',
          lineHeight: '1.3',
          borderLeft: '8px solid #cc0000',
          paddingLeft: '30px',
          background: 'linear-gradient(90deg, rgba(204,0,0,0.1) 0%, transparent 100%)'
        }}>
          {trial.title}
        </div>

        {/* Results panel */}
        <div style={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
          border: '2px solid #333',
          borderRadius: '12px',
          padding: '50px',
          marginBottom: '40px'
        }}>
          {/* Winner */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px',
            paddingBottom: '30px',
            borderBottom: '3px solid #cc0000'
          }}>
            <div>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#cc0000',
                marginBottom: '10px',
                textTransform: 'uppercase',
                letterSpacing: '2px'
              }}>
                VERDICT:
              </div>
              <div style={{
                fontSize: isStory ? '56px' : '72px',
                fontWeight: '900',
                color: '#fff'
              }}>
                {winner}
              </div>
            </div>
            <div style={{
              fontSize: isStory ? '100px' : '140px',
              fontWeight: '900',
              color: '#cc0000',
              lineHeight: '1'
            }}>
              {winnerPct}%
            </div>
          </div>

          {/* Loser */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            opacity: 0.4
          }}>
            <div style={{
              fontSize: isStory ? '36px' : '42px',
              fontWeight: '700',
              textDecoration: 'line-through'
            }}>
              {loser}
            </div>
            <div style={{
              fontSize: isStory ? '56px' : '72px',
              fontWeight: '900'
            }}>
              {100 - winnerPct}%
            </div>
          </div>
        </div>

        {/* Reporter quote */}
        <div style={{
          background: 'rgba(204,0,0,0.1)',
          borderLeft: '6px solid #cc0000',
          padding: '30px',
          fontSize: isStory ? '28px' : '32px',
          fontWeight: '600',
          fontStyle: 'italic',
          lineHeight: '1.5'
        }}>
          "{punchline}"
        </div>
      </div>

      {/* News ticker at bottom */}
      <div style={{
        background: '#cc0000',
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 -4px 20px rgba(204,0,0,0.8)'
      }}>
        <div style={{ fontSize: '18px', fontWeight: '700', textTransform: 'uppercase' }}>
          GWEFFIT NEWS NETWORK
        </div>
        <div style={{ fontSize: '18px', fontWeight: '700' }}>
          {trial.totalVotes || 0} VERIFIED JURORS
        </div>
        <div style={{ fontSize: '18px', fontWeight: '700' }}>
          GWEFFIT.COM
        </div>
      </div>
    </div>
  );
};

// =====================================================
// SMART TEMPLATE SELECTOR
// =====================================================
export const selectBestTemplate = (trial, verdict) => {
  const title = trial.title?.toLowerCase() || '';
  const sideA = trial.sideA?.toLowerCase() || '';
  const sideB = trial.sideB?.toLowerCase() || '';
  const content = `${title} ${sideA} ${sideB}`;
  
  // Sports keywords
  const sportsKeywords = ['goat', 'messi', 'ronaldo', 'jordan', 'lebron', 'federer', 'nadal', 'brady', 'nba', 'nfl', 'soccer', 'football', 'basketball', 'tennis', 'baseball', 'athlete', 'player', 'team', 'championship', 'league'];
  
  // Brutal/savage keywords
  const savageKeywords = ['destroy', 'kill', 'annihilate', 'crush', 'dominate', 'obliterate', 'wreck', 'roast', 'savage', 'brutal', 'worst', 'overrated', 'trash', 'terrible'];
  
  // Wholesome keywords
  const wholesomeKeywords = ['better', 'prefer', 'favorite', 'best', 'love', 'pizza', 'food', 'breakfast', 'coffee', 'tea', 'cute', 'nice', 'wholesome'];
  
  // News/serious keywords
  const newsKeywords = ['should', 'policy', 'law', 'government', 'economy', 'climate', 'education', 'healthcare', 'privacy', 'data', 'rights'];
  
  // Check for keyword matches
  const hasSports = sportsKeywords.some(kw => content.includes(kw));
  const hasSavage = savageKeywords.some(kw => content.includes(kw));
  const hasWholesome = wholesomeKeywords.some(kw => content.includes(kw));
  const hasNews = newsKeywords.some(kw => content.includes(kw));
  
  // Check percentage margin for brutality
  const margin = Math.abs((verdict.winnerPct || 50) - 50);
  const isBlowout = margin > 30; // 80%+ or 20%- is a blowout
  
  // Selection logic
  if (hasSports) return 'sports';
  if (hasSavage || isBlowout) return 'savage';
  if (hasNews) return 'news';
  if (hasWholesome) return 'wholesome';
  
  // Default to courtroom for everything else
  return 'courtroom';
};

// =====================================================
// TEMPLATE REGISTRY
// =====================================================
export const TEMPLATES = {
  courtroom: {
    name: 'Official Courtroom',
    description: 'Formal verdict with gold accents',
    component: CourtroomTemplate,
    icon: '⚖️'
  },
  sports: {
    name: 'Sports ESPN',
    description: 'Bold athletic style with scores',
    component: SportsTemplate,
    icon: '🏆'
  },
  savage: {
    name: 'Savage Roast',
    description: 'Dark and brutal with flames',
    component: SavageTemplate,
    icon: '🔥'
  },
  wholesome: {
    name: 'Wholesome Fun',
    description: 'Bright and cheerful celebration',
    component: WholesomeTemplate,
    icon: '🎉'
  },
  news: {
    name: 'Breaking News',
    description: 'Urgent news ticker style',
    component: BreakingNewsTemplate,
    icon: '📰'
  }
};